// frontend/packages/common/api/Widgets/GiftVoucher/GiftVouchers.js
import {useState, useRef, useEffect} from 'react';
import axiosWidgetInstance, {withApiKey, widgetEndpoints} from 'packages/common/utils/axiosWidget';
import {widgetLogger} from 'packages/common/utils/widgetLogger';

/**
 * Hook pour récupérer les types de bons cadeaux via l'API externe
 * @param {string} apiKey - Clé API pour l'authentification
 * @return {Object} - Types de bons cadeaux, états de chargement et erreurs
 */
export const useExternalGiftVoucherTypes = (apiKey) => {
  const [isLoading, setIsLoading] = useState(true);
  const [isError, setIsError] = useState(false);
  const [error, setError] = useState(null);
  const [giftVoucherTypes, setGiftVoucherTypes] = useState([]);

  useEffect(() => {
    const fetchGiftVoucherTypes = async () => {
      // Reset states
      setIsLoading(true);
      setIsError(false);
      setError(null);

      if (!apiKey) {
        const apiError = new Error('API key is missing');
        widgetLogger.warn('🎁 Gift Vouchers : API key manquante');
        setError(apiError);
        setIsLoading(false);
        setIsError(true);
        return;
      }

      try {
        const response = await axiosWidgetInstance.get(
            widgetEndpoints.externalApi.giftVoucher.catalog,
            withApiKey(apiKey),
        );

        setGiftVoucherTypes(response.data?.data || []);
        setIsError(false);
      } catch (apiError) {
        // Les logs d'erreur sont gérés par l'intercepteur axios
        setError(apiError);
        setIsError(true);
      } finally {
        setIsLoading(false);
      }
    };

    fetchGiftVoucherTypes();
  }, [apiKey]);

  return {
    giftVoucherTypes,
    isLoading,
    isError,
    error, // Expose the actual error
  };
};

/**
 * Hook pour récupérer les catégories de catalogue via l'API externe
 * @param {string} apiKey - Clé API pour l'authentification
 * @return {Object} - Catégories, états de chargement et erreurs
 */
export const useExternalCatalogCategories = (apiKey) => {
  const [isLoading, setIsLoading] = useState(true);
  const [isError, setIsError] = useState(false);
  const [error, setError] = useState(null);
  const [categories, setCategories] = useState([]);

  const isFirstMount = useRef(true);
  const retryCount = useRef(0);
  const MAX_RETRIES = 2; // Réduire les retry pour éviter le spam

  useEffect(() => {
    if (!apiKey) {
      const apiError = new Error('API key is missing for categories');
      widgetLogger.warn('🏷️ Categories : API key manquante');
      setError(apiError);
      setIsLoading(false);
      setIsError(true);
      return;
    }

    let isMounted = true;

    const fetchCategoriesWithRetry = async () => {
      try {
        const response = await axiosWidgetInstance.get(
            widgetEndpoints.externalApi.giftVoucher.categoriesGeneral,
            withApiKey(apiKey),
        );

        if (isMounted) {
          setCategories(response.data?.data || []);
          setIsError(false);
          setError(null);
          retryCount.current = 0;
        }
      } catch (apiError) {
        if (!isMounted) return;

        // Retry logic pour rate limiting seulement
        if (apiError.response?.status === 429 && retryCount.current < MAX_RETRIES) {
          const delay = Math.pow(2, retryCount.current) * 1000;
          retryCount.current++;
          widgetLogger.warn(`Rate limited. Retrying in ${delay}ms (attempt ${retryCount.current})`);

          setTimeout(fetchCategoriesWithRetry, delay);
          return;
        }

        // Les autres logs d'erreur sont gérés par l'intercepteur axios
        setError(apiError);
        setIsError(true);
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };

    // Initial delay to avoid rate limiting
    const timer = setTimeout(() => {
      fetchCategoriesWithRetry();
    }, isFirstMount.current ? 300 : 0);

    isFirstMount.current = false;

    return () => {
      isMounted = false;
      clearTimeout(timer);
    };
  }, [apiKey]);

  return {
    categories,
    isLoading,
    isError,
    error, // Expose the actual error
  };
};
